/**
 * Calendar state persistence using sessionStorage.
 * Preserves selected date, view, and filters when navigating away and back.
 */

import { IAppointmentFilters } from "../types/appointments.types";
import type { ViewType } from "../types/appointments.types";

const STORAGE_KEY = "bpreven-admin-calendar-state";

export interface ICalendarStoredState {
  view: ViewType;
  date: string; // ISO date string (YYYY-MM-DD)
  filters: IAppointmentFilters;
}

/** Parse YYYY-MM-DD as local midnight (avoids UTC timezone shift). */
export function parseDateStringLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return new Date(NaN);
  return new Date(y, m - 1, d);
}

/** Format date as YYYY-MM-DD using local date components (avoids UTC shift). */
export function toDateStringLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseStoredDate(iso: string): Date | null {
  if (!iso) return null;
  const d = parseDateStringLocal(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function getStoredCalendarState(): ICalendarStoredState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ICalendarStoredState;
    if (!parsed.view || !parsed.date) return null;
    if (!parseStoredDate(parsed.date)) return null;
    return {
      view: parsed.view,
      date: parsed.date,
      filters: parsed.filters || {},
    };
  } catch {
    return null;
  }
}

export function setStoredCalendarState(state: Partial<ICalendarStoredState>): void {
  try {
    const current = getStoredCalendarState() || {
      view: "daily",
      date: toDateStringLocal(new Date()),
      filters: {},
    };
    const merged: ICalendarStoredState = {
      view: state.view ?? current.view,
      date: state.date ?? current.date,
      filters: state.filters ?? current.filters,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // Ignore storage errors
  }
}

export function getStoredDateForView(view: ViewType): Date | null {
  const stored = getStoredCalendarState();
  if (!stored || stored.view !== view) return null;
  return parseStoredDate(stored.date);
}

export function getInitialDateForView(view: ViewType): Date {
  const stored = getStoredDateForView(view);
  if (stored) {
    if (view === "weekly") {
      // fix bug when switching from daily to weekly, the week start was not the correct one
      const day = stored.getDay();
      const weekStart = new Date(stored);
      weekStart.setDate(stored.getDate() - day);
      return weekStart;
    }
    return stored;
  }

  const now = new Date();
  if (view === "daily") return now;

  // monthly
  return now;
}

/** Save date to storage for a given view. Uses local date to avoid timezone shift. */
export function saveDateForView(view: ViewType, date: Date): void {
  let dateStr: string;
  if (view === "daily") {
    dateStr = toDateStringLocal(date);
  } else if (view === "weekly") {
    // Store week start (Sunday)
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    dateStr = toDateStringLocal(d);
  } else {
    // monthly - store first of month
    dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
  }
  setStoredCalendarState({ view, date: dateStr });
}
